import axios from 'axios'
import store from '@/store'
import Cookie from '@/services/cookie'
import { useSignature } from '@/stores/signatures'
import { useUsersStore } from '@/stores/users'
import * as Sentry from "@sentry/vue";
import { useReseller } from '@/stores/reseller'
import { useConfigurations } from '@/stores/configurations'
import { notify } from '@kyvg/vue3-notification'
import { loadInternalChat, removeInternalChat } from '@/plugins/internal-chat'
import { useRepresentationStore } from '@/stores/representation'
import useHelpers from '@/composables/useHelpers'
import ChatmixData from '@/assets/js/dash/ChatmixData'
import checkModuleAccess from './checkModuleAccess'
import guardReportsOwnMode from './guardReportsOwnMode'
import { shouldBlockAccess } from '@/utils/accessTime'

const checkToRedirect = (path) => {
    const origin = window.location.origin
    let redirectTo = `login?unauthenticated=true&redirect=${path}`

    if (!origin.includes('localhost') || !origin.includes('127.0.0.1')) {
        redirectTo = `v2/${redirectTo}`
    }

    return `${origin}/${redirectTo}`
}

function getRemoveExcessBearer(token) {
    const newToken = token.split(' ')
    const index = newToken.length - 1
    return newToken[index]
}

/** Evita POST repetido em navegações SPA seguidas (mesmo token). */
const CHECK_ACCESS_CACHE_MS = 90 * 1000
let checkAccessCache = { at: 0, tokenKey: '' }

function ensureInternalChatForRoute(to) {
    const ignoreLoadInternalChat = [
        'auth.confirm_signature',
    ]

    if (!ignoreLoadInternalChat.includes(to.name)) {
        let internalChatMode
        switch (to.path) {
            case '/chat-interno':
                internalChatMode = 'full'
                break
            default:
                internalChatMode = 'button'
        }
        loadInternalChat(internalChatMode)
    }
}

export default {
    async redirectIfNotAuthenticated(to, from, next) {
        const token = Cookie.getAuthToken()
        const representationStore = useRepresentationStore()
        if (!token) {
            next(false)
            notify({
                title: 'Acesso negado',
                text: 'Você precisa estar logado para acessar essa página.',
                type: 'error',
                duration: 5000,
            })

            removeInternalChat()

            window.location.href = checkToRedirect(to.path)

            next(false)
            return
        }

        const now = Date.now()
        const cacheFresh =
            checkAccessCache.tokenKey === token &&
            (now - checkAccessCache.at) < CHECK_ACCESS_CACHE_MS

        if (cacheFresh) {
            ensureInternalChatForRoute(to)
            representationStore.checkToken()

            if (to.name !== 'expired-access' && to.path !== '/acesso-expirado') {
                const usersStore = useUsersStore()
                if (shouldBlockAccess(usersStore.me)) {
                    next({ name: 'expired-access' })
                    return
                }
            }

            next()
            return
        }

        const BASEURL = axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT || window.location.origin + '/api_v2/api'

        await axios.post(BASEURL + '/v1/auth/check_access', { token: token })
            .then(response => {
                const data = response.data

                const signatureStore = useSignature()

                const envData = import.meta.env
                let chatColorPrimary = envData.VITE_CHAT_COLOR_PRIMARY
                let chatColorShadow = envData.VITE_CHAT_COLOR_SHADOW
                let chatFavicon = ChatmixData.logo.compact

                if (data.data.user.status === 'blocked') {
                    checkAccessCache = { at: 0, tokenKey: '' }
                    // TODO: Redirect to logout?
                    alert('Assinatura bloqueada!')
                    // window.location.href = window.location.origin
                    next(false)
                    return
                }

                const usersStore = useUsersStore()
                if (!store?.state?.user?.id) {
                    usersStore.user = data.data.user
                    usersStore.user.access_time = data.data?.access_time
                    Sentry.setUser({ id: data.data.user.id, email: data.data.user.email })
                    store.commit('user/STORE_USER', data.data.user)
                } else if (data.data?.access_time) {
                    usersStore.user.access_time = data.data.access_time
                }

                if (data.data?.business) {
                    const store = useConfigurations()
                    store.business = data.data?.business
                }

                if (data.data.reseller) {
                    chatFavicon = data.data.reseller.logo_chat
                    const resellerStore = useReseller()
                    resellerStore.data = data.data.reseller
                    if (!store?.state?.reseller?.id) {
                        store.commit('reseller/STORE_RESELLER', data.data.reseller)
                    }
                }

                if (data.attendants) {
                    if (!store?.state?.attendants) {
                        store.commit('attendants/STORE_ATTENDANTS', data.data.attendants)
                    }
                }

                if (data.channels) {
                    if (!store?.state?.channels) {
                        store.commit('channels/STORE_CHANNELS', data.data.channels)
                    }
                }

                // Set new token
                if (data.access_token) {
                    // Exists representation token
                    // if (!Cookie.getAuthToken('_chat_auth_representation')) {
                    const newToken = getRemoveExcessBearer(data.access_token)
                    const setToken = `${data.type_token} ${newToken}`
                    Cookie.setToken(setToken)
                    // }
                }

                if (data.data.signature) {
                    signatureStore().signatureChat = data.data.signature
                }

                if (data.data.signature_status) {
                    if (to.name !== 'auth.confirm_signature' && data.data.signature_status.status === 'expired') {
                        next({ name: 'auth.confirm_signature' })
                        return
                    }

                    signatureStore().signatureStatus = data.data.signature_status
                }

                ensureInternalChatForRoute(to)

                // Custom all pages
                document.body.style.setProperty('--chatPrimary', chatColorPrimary)
                document.body.style.setProperty('--chatShadow', chatColorShadow)
                document.querySelector('link[rel*=\'icon\']').href = chatFavicon

                checkAccessCache = {
                    at: Date.now(),
                    tokenKey: Cookie.getAuthToken() || token,
                }

                representationStore.checkToken()

                if (to.name !== 'expired-access' && to.path !== '/acesso-expirado') {
                    if (shouldBlockAccess(usersStore.me)) {
                        next({ name: 'expired-access' })
                        return
                    }
                }

                next()
            })
            .catch(e => {
                checkAccessCache = { at: 0, tokenKey: '' }
                console.error(e)
                const res = e.response?.data

                if (!res?.error) {
                    notify({
                        type: 'error',
                        title: 'Erro interno!',
                        text: 'Por favor, acione o suporte e comunique o erro: checkAccessMiddlewareApp',
                        duration: 25000,
                    })

                    next(false)
                    return
                }

                switch (res.error) {
                    case 'OperatingHoursInvalidException':
                        notify({
                            type: 'error',
                            title: 'Atenção, Acesso Bloqueado!',
                            text: 'Horário para você acessar foi bloqueado pelo administrador da empresa!',
                            duration: 25000,
                        })

                        next({ name: 'auth.logoff' })
                        break

                    case 'SignatureBlockedException':
                        notify({
                            type: 'error',
                            title: 'Acesso negado!',
                            text: 'O estado atual da assinatura não permite que acesse essa página. Por favor, acione o suporte!',
                            duration: 25000,
                        })

                        next({ name: 'auth.logoff' })
                        break

                    case 'LoginInvalidException':
                        notify({
                            type: 'error',
                            title: 'Acesso negado!',
                            text: 'Usuário e/ou senha inválido. Por favor, faça login novamente!',
                            duration: 25000,
                        })

                        next({ name: 'auth.logoff' })
                        break
                    // ...
                }

                next(false)
            })
    },

    checkPermission(to, from, next) {
        let access = true

        const { checkPermission } = useHelpers()
        const usersStore = useUsersStore()
        const representationStore = useRepresentationStore()

        if (usersStore.me.level === 1 && to?.meta?.middleware) {
            access = false

            if (usersStore.me?.permissions) {
                access = usersStore.me.permissions.includes(to.meta.middleware)
            }
        }

        if (to?.meta?.permission && !checkPermission(to.meta.permission, store.state.user.user.level) && !access) {

            console.warn('Chatmix: Access not allowed!')

            notify({ type: 'error', title: 'Acesso negado!', text: 'Você não tem permissão para acessar essa página!' })

            setTimeout(() => {
                if (!from?.name) {
                    next({ name: 'expired-blocked' })
                }
            }, 1000)
            return
        }
        representationStore.checkToken()
        next()
    },

    checkAutomationAccess(to, from, next) {
        const signatureStore = useSignature()

        const accessAutomation = signatureStore().accessAutomation
        if (!accessAutomation) {
            notify({ type: 'error', title: 'Acesso negado!', text: 'Você não tem permissão para acessar essa página!' })

            setTimeout(() => {
                if (!from?.name) {
                    next({ name: 'expired-blocked' })
                }
            }, 1000)
            return
        }

        next()
    },

    checkController(to, from, next) {
        const usersStore = useUsersStore()
        const resellerStore = useReseller()

        const businessVmix = usersStore.me.business_id === 6
        const isReseller = resellerStore.data?.business === usersStore.me.business_id
        if (businessVmix || isReseller) {
            next()
            return
        }

        notify({ type: 'error', title: 'Acesso negado!', text: 'Você não tem permissão para acessar essa página!' })

        setTimeout(() => {
            if (!from?.name) {
                next({ name: 'expired-blocked' })
            }
        }, 1000)
    },

    checkIfLogin(to, from, next) {
        const token = Cookie.getAuthToken()

        console.warn(token, 'token')

        if (!token) {
            notify({
                title: 'Acesso negado',
                text: 'Você precisa estar logado para acessar essa página.',
                type: 'error',
                duration: 5000,
            })

            removeInternalChat()

            window.location.href = checkToRedirect(to.path)

            next(false)
        }

        next()
    },

    async checkResellerData(to, from, next) {
        const url = window.location.origin

        await axios.post('v1/auth/check-site', { url: url })
            .then((response) => {
                // verifico se existe conteudo em response.data
                if (response.data.data) {

                    console.log(response.data)
                    const data = response.data
                    const resellerStore = useReseller()
                    resellerStore.data = data.data
                }
            })

        next()
    },

    checkExistsToken(to, from, next) {
        const token = to.query?.token
        if (!token) {
            next({ name: 'auth.login' })
        }

        next()
    },

    checkAccessTime(to, from, next) {
        if (to.name === 'expired-access' || to.path === '/acesso-expirado') {
            return next()
        }

        const usersStore = useUsersStore()
        if (shouldBlockAccess(usersStore.me)) {
            return next({ name: 'expired-access' })
        }

        next()
    },

    checkModuleAccess,

    guardReportsOwnMode,
}
